import queryString from 'query-string';

import fetchQuery from 'rebook/apps/utils/fetchQuery';
import * as SeoActions from 'rebook/apps/base/actions/SeoActions';
import cropStorageImage from 'rebook/apps/utils/urls/cropStorageImage';
import * as Error404Actions from 'rebook/apps/base/actions/Error404Actions';
import truncateChars from 'rebook/apps/utils/text/truncateChars';
import getPageNumberString from 'rebook/apps/utils/text/getPageNumberString';
import {
  BOOKS_BOOK_REVIEWS_SET_DATA,
  BOOKS_BOOK_REVIEWS_RESET_DATA,
} from 'rebook/apps/books/constants';
import { Book } from 'rebook/apps/books/models';


export function fetchInitialData(match, location = {}) {
  return async(dispatch, getState) => {
    const { ssr, bookReviewList: currentState } = getState();
    const pageNumber = ssr.query && ssr.query.page
      ? parseInt(ssr.query.page, 10)
      : 1;

    const { bookSlug } = match.params;

    let newState = {
      bookSlug,
    };
    if (location && location.state) {
      newState = {
        ...newState,
        ...location.state,
      };
    }
    if (currentState.reviews) {
      newState = {
        ...newState,
        ...currentState,
      };
    }

    // set partial state (it could probably be the data obtained from router)
    dispatch(setData(newState));

    if (!newState.book) {
      await dispatch(fetchBook(match));
    }
    if (!newState.reviews || currentState.pageNumber !== pageNumber) {
      await dispatch(fetchReviewList(pageNumber));
    }
  };
}

export function fetchBook(match) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    const { bookSlug } = match.params;
    const url = `/api/books/s-${bookSlug}/`;
    return await fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
      .then(
        ({ data }) => {
          dispatch(setData({ book: data }));
        }
      )
      .catch(
        ({ data, resp, error }) => {
          if (resp.status === 404) {
            dispatch(Error404Actions.show());
          }
          else {
            Raven.captureMessage('failed to load review list book due to server error', {
              level: 'error',
              extra: {
                status: resp.status,
                response: data,
                error,
              },
            });
            throw new Error('failed to load review list book detail due to server error');
          }
        }
      );
  };
}

export function fetchReviewList(pageNumber = 1) {
  return async(dispatch, getState) => {
    const { bookReviewList, ssr } = getState();
    const { book } = bookReviewList;
    if (!book) {
      return;
    }
    // optimistically update the UI
    dispatch(setData({
      reviews: null,
      pageNumber,
    }));
    const limit = 10;
    let queryParams = {
      book: book.id,
      html: 1,
      limit,
      offset: (pageNumber - 1) * limit,
    };
    const qs = queryString.stringify(queryParams);
    const url = `/api/book-reviews/?${qs}`;
    return await fetchQuery(url, { headers: ssr.headers })
      .then(
        ({ data }) => {
          const lastPageNumber = Math.ceil(data.meta.total_count / limit);
          dispatch(setData({
            reviews: data.objects,
            pageNumber,
            lastPageNumber,
          }));
        }
      )
      .catch(
        ({ data, resp, error }) => {
          Raven.captureMessage('failed to load review list due to server error', {
            level: 'error',
            extra: {
              status: resp.status,
              response: data,
              error,
            },
          });
          throw new Error('failed to load review list detail due to server error');
        }
      );
  };
}

function setSeoMeta() {
  return (dispatch, getState) => {
    const { bookReviewList } = getState();
    const { pageNumber, reviews } = bookReviewList;
    const book = new Book(bookReviewList.book);
    const cover200x300 = cropStorageImage(book.default_cover, 200, 300);
    const desktopUrl = `https://${BASE_HOST}${book.getReviewsUrl()}`;
    const bookUrl = `https://${BASE_HOST}${book.absolute_url}`;
    const title = `«${book.name}» отзывы и рецензии читателей на книгу автора ` +
        `${book.main_author ? ` ${book.main_author.genitive_full_name || book.main_author.cover_name}` : '' } `+
        `, рейтинг книги — MyBook. ${getPageNumberString(pageNumber)}`.trim();
    const description = truncateChars(
      `Читайте интересные рецензии и отзывы читателей на книгу «${book.name}»` +
      `${book.main_author ? `, ${book.main_author.cover_name}.` : '' }` +
      ` Более ${book.reviews_count}` +
      ` отзывов и рецензий, рейтинг книги по версии читателей электронной библиотеке MyBook.ru`,
      300
    );
    const ogTitle = `Отзывы читателей о книге «${book.name}» отзывы и рецензии читателей на книгу автора` +
      `${book.main_author ? ` — ${book.main_author.cover_name}` : '' }` +
      `${getPageNumberString(pageNumber)} — MyBook`;
    const ogDescription = truncateChars(
      `${reviews.length > 0 && reviews[0].comment_plain ? reviews[0].comment_plain : `${book.annotation_plain}`}`,
      300
    );
    const ogTags = [
      { name: 'type', value: 'books.book' },
      { name: 'image', value: cover200x300 },
      { name: 'title', value: ogTitle },
      { name: 'description', value: ogDescription },
      { name: 'url', value: desktopUrl },
    ];
    const ogTwitterTags = [
      { name: 'title', value: ogTitle },
      { name: 'description', value: ogDescription },
      { name: 'image', value: cover200x300 },
    ];
    let ogBookTags = [
      { name: 'rating:value', value: book.rating.rating ? book.rating.rating : 0 },
      { name: 'rating:scale', value: 5 },
    ];
    if (book.ean) {
      ogBookTags.push({
        name: 'isbn',
        value: book.ean,
      });
    }
    if (book.main_author) {
      ogBookTags.push({
        name: 'author',
        value: `https://${BASE_HOST}${book.main_author.absolute_url}`,
      });
    }
    dispatch(SeoActions.setData({
      imageSrc: cover200x300,
      canonicalUrl: reviews.length ? desktopUrl : bookUrl,
      title,
      description,
      ogTags,
      ogBookTags,
      ogTwitterTags,
      pageNumber,
    }));
  };
}

export function setData(payload) {
  return (dispatch) => {
    const { reviews } = payload;
    dispatch({
      type: BOOKS_BOOK_REVIEWS_SET_DATA,
      payload,
    });
    if (reviews) {
      dispatch(setSeoMeta());
    }
  };
}

export function resetData() {
  return (dispatch) => {
    dispatch(SeoActions.resetData());
    dispatch({
      type: BOOKS_BOOK_REVIEWS_RESET_DATA,
    });
  };
}
